import React from 'react'
import { type MessageDescriptor, useIntl } from 'react-intl'
import {
  middleDotSeparatorMessage,
  pipeSeparatorMessage,
  bulletSeparatorMessage,
  dashSeparatorMessage,
} from 'LuxKit/intl/separators'

const separatorMessages = {
  middot: middleDotSeparatorMessage,
  pipe: pipeSeparatorMessage,
  bullet: bulletSeparatorMessage,
  dash: dashSeparatorMessage,
} as const

type SeparatorVariant = keyof typeof separatorMessages

interface Props {
  /** A built-in variant name, or a custom MessageDescriptor */
  variant: SeparatorVariant | MessageDescriptor
  /** Render a space before the separator character */
  leftSpace?: boolean
  /** Render a space after the separator character */
  rightSpace?: boolean
}

/**
 * Renders a decorative inline separator character (e.g. `·`, `|`, `•`).
 *
 * Marked `aria-hidden` since the separator is purely visual.
 * Spacing can be optionally included as `leftSpace` and `rightSpace` props.
 *
 * To add a new built-in variant:
 * 1. Define a message in `LuxKit/intl/separators.ts` using `defineMessage()`
 * 2. Add it to the `separatorMessages` map above
 *
 * For one-off separators, pass a `MessageDescriptor` directly as `variant`.
 *
 * @example
 * ```tsx
 * <span>Sydney</span>
 * <Separator variant="middot" />
 * <span>3 nights</span>
 * ```
 */
function Separator({ variant, leftSpace, rightSpace }: Props) {
  const intl = useIntl()
  const message = typeof variant === 'string'
    ? separatorMessages[variant]
    : variant
  // TODO: use css direction agnostic spacing instead
  return <span aria-hidden="true">{leftSpace && ' '}{intl.formatMessage(message)}{rightSpace && ' '}</span>
}

export default Separator
